import moment from 'moment';

import * as Sentry from '@sentry/vue';
import { AxiosResponse } from 'axios';
import { Queue } from '../globals/queue';
import { Timespan } from './timespans';
import { BaseService } from './_base';

import { getLocaleDatetimeString } from '../globals/helpers';
import { v4 as uuidv4 } from 'uuid';
import { connection } from '../globals/internet';
import { Ref, ref } from 'vue';
import { localStorageMutex, MUTEX_NOT_ACQUIRED } from '../globals/mutex';
import { TagAssignment } from './projects';


interface EmployeeTrack {
    id: string;
}


interface NewTimestamp {
    id: string;
    employee_id: string;
    time: string;
    stamp_type: string;

    description?: string;
    tag_assignments?: TagAssignment[];
    meta: any;
}

interface UpdateTimestamp {
    id: string;
    employee_id: string;
    description?: string;
    tag_assignments?: TagAssignment[];
}

interface OfflineAction {
    token: string;
    action: string;
    timestamp: NewTimestamp|UpdateTimestamp;
}


class TrackingService extends BaseService<EmployeeTrack> {
    queue: Queue<OfflineAction>;
    isQueueProcessing: boolean;
    processedItems: Ref<number>;

    constructor() {
        super("/api/v2/tracking/employees/");
        this.cacheTimeout = 5;
        this.queue = new Queue<OfflineAction>('tracking');
        this.isQueueProcessing = false;
        this.processedItems = ref(0);
    }

    addTimestamp(employeeId: string, comment?: string, tagAssignments?: TagAssignment[], meta?: any, authToken?: string, stampType?: string) {
        return this._post(employeeId + '/add-timestamp/', {
            description: comment,
            tag_assignments: tagAssignments,
            meta: meta,
            stamp_type: stampType,
        }, authToken);
    }

    getQueuedActions() {
        return this.queue.getQueue();
    }

    async addTimestampToQueue(employeeId: string, intent: string, comment?: string, tagAssignments?: TagAssignment[], meta?: any, time?: string) {
        // Ensure the last element in the queue differs from the new one
        const mostRecentlyAdded = this.queue.mostRecentlyAdded();
        if (mostRecentlyAdded && mostRecentlyAdded.timestamp.stamp_type === intent && mostRecentlyAdded.timestamp.employee_id === employeeId) {
            return mostRecentlyAdded;
        };

        const localId = "$local|" + uuidv4();
        meta = meta || {};
        meta.local_id = localId;
        const ts = {
            id: localId,
            employee_id: employeeId,
            time: time || getLocaleDatetimeString(),
            stamp_type: intent,
            description: comment,
            tag_assignments: tagAssignments,
            meta: meta,
        };
        const state = await localStorageMutex('services__tracking__processQueue', async () => {
            this.queue.push({
                token: localStorage.accessToken as string,
                action: 'add',
                timestamp: ts,
            });
        }, 10 * 1000);

        if (state === MUTEX_NOT_ACQUIRED) return null;

        return ts;
    }

    async addTimestampUpdateToQueue(employeeId: string, checkinTimestampId: string, description?: string, tagAssignments?: TagAssignment[]) {
        await localStorageMutex('services__tracking__processQueue', async () => {
            // Find timestamp in queue and update its values
            const queue = this.queue.getQueue();
            for (const action of queue) {
                if (action.timestamp.id !== checkinTimestampId) continue;
                if (description !== undefined) action.timestamp.description = description;
                if (tagAssignments !== undefined) action.timestamp.tag_assignments = tagAssignments;
                this.queue.commitQueue(queue);
                return;
            }

            // If timestamp is not in queue, add a change action
            this.queue.push({
                token: localStorage.accessToken as string,
                action: 'update',
                timestamp: {
                    id: checkinTimestampId,
                    employee_id: employeeId,
                    description: description,
                    tag_assignments: tagAssignments,
                }
            });
        }, 10 * 1000);
    }

    updateComment(employeeId: string, comment?: string, tagAssignments?: TagAssignment[]) {
        return this._post(employeeId + '/update-comment-and-tag-assignments/', {
            "description": comment,
            "tag_assignments": tagAssignments,
        });
    }

    updateTimespan(employeeId: string, timestampId: string, comment?: string, tagAssignments?: TagAssignment[]) {
        return this._post(employeeId + '/update-timespan/', {
            "timestamp_id": timestampId,
            "description": comment,
            "tag_assignments": tagAssignments,
        });
    }

    activeTimespan(employeeId: string): Promise<AxiosResponse<Timespan|string>> {
        return this._get(employeeId + '/active-timespan/') as unknown as Promise<AxiosResponse<Timespan|string>>;
    }

    async processQueue() {
        if (!connection.isConnected.value) return;

        // Only acquire a mutex if there's at least one item to process
        if (!this.queue.peek()) return;

        localStorageMutex('services__tracking__processQueue', async () => {
            try {
                let processedItems = 0;
                while (this.queue.peek()) {
                    const action = this.queue.peek();
                    if (action?.action === 'add') {
                        action.timestamp.meta.tracked_time = action.timestamp.time;
                        const start = Date.now();
                        await this.addTimestamp(action.timestamp.employee_id, action.timestamp.description, action.timestamp.tag_assignments, action.timestamp.meta, action.token, action.timestamp.stamp_type);
                        Sentry.addBreadcrumb({
                            category: 'tracking',
                            type: 'http',
                            level: 'info',
                            message: `add-timestamp POST succeeded (${action.timestamp.stamp_type})`,
                            data: {
                                duration_ms: Date.now() - start,
                                stamp_type: action.timestamp.stamp_type,
                                local_id: action.timestamp.id,
                                employee_id: action.timestamp.employee_id,
                                queue_remaining: this.queue.count() - 1,
                            },
                        });
                        this.queue.pop();
                        processedItems += 1;
                    } else if (action?.action === 'update') {
                        const start = Date.now();
                        await this.updateTimespan(action.timestamp.employee_id, action.timestamp.id, action.timestamp.description, action.timestamp.tag_assignments);
                        Sentry.addBreadcrumb({
                            category: 'tracking',
                            type: 'http',
                            level: 'info',
                            message: 'update-timespan POST succeeded',
                            data: {
                                duration_ms: Date.now() - start,
                                timestamp_id: action.timestamp.id,
                                employee_id: action.timestamp.employee_id,
                                queue_remaining: this.queue.count() - 1,
                            },
                        });
                        this.queue.pop();
                        processedItems += 1;
                    } else {
                        console.warn('Unknown action: ', action);
                        await new Promise(resolve => setTimeout(resolve, 10000));
                    }
                }
                if (processedItems > 0) {
                    this.processedItems.value += processedItems;
                }

            } catch (error) {
                Sentry.captureException(error, { tags: { source: 'TrackingService.processQueue' } });
                console.log('Error: ', error);
            }
        });
    }
}

const trackingService = new TrackingService();

export { trackingService };
